import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface ApiError {
  code: string;
  message: string;
  details?: any;
  retryable?: boolean;
}

export interface RetryConfig {
  maxRetries?: number;
  retryDelay?: number;
  backoffMultiplier?: number;
  retryableErrors?: string[];
}

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  backoffMultiplier: 2,
  retryableErrors: [
    'NETWORK_ERROR',
    'TIMEOUT_ERROR',
    'SERVER_ERROR',
    'SERVICE_UNAVAILABLE',
    'HTTP_500',
    'HTTP_502',
    'HTTP_503',
    'HTTP_504',
  ],
};

export function useErrorHandler() {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const { signOut } = useAuth();

  const isRetryableError = useCallback((error: ApiError, config: RetryConfig = {}): boolean => {
    const retryableErrors = config.retryableErrors || DEFAULT_RETRY_CONFIG.retryableErrors;
    return retryableErrors.includes(error.code) || error.retryable === true;
  }, []);

  const getErrorMessage = useCallback((error: ApiError): string => {
    // Provide user-friendly error messages
    const errorMessages: Record<string, string> = {
      'NETWORK_ERROR': 'Unable to connect to the server. Please check your internet connection and try again.',
      'TIMEOUT_ERROR': 'The request took too long to complete. Please try again.',
      'AUTH_REQUIRED': 'Please sign in to continue.',
      'UNAUTHORIZED': 'You don\'t have permission to perform this action.',
      'TOKEN_EXPIRED': 'Your session has expired. Please sign in again.',
      'VALIDATION_ERROR': 'Please check your input and try again.',
      'NOT_FOUND': 'The requested item could not be found.',
      'CONFLICT': 'This action conflicts with existing data. Please refresh and try again.',
      'SERVER_ERROR': 'A server error occurred. Please try again later.',
      'SERVICE_UNAVAILABLE': 'The service is temporarily unavailable. Please try again later.',
      'RATE_LIMITED': 'Too many requests. Please wait a moment and try again.',
    };

    return errorMessages[error.code] || error.message || 'An unexpected error occurred';
  }, []);

  const handleAuthError = useCallback(async (error: ApiError): Promise<void> => {
    const authErrors = ['AUTH_REQUIRED', 'UNAUTHORIZED', 'TOKEN_EXPIRED'];
    if (authErrors.includes(error.code)) {
      await signOut();
    }
  }, [signOut]);

  const sleep = useCallback((ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  }, []);

  const executeWithRetry = useCallback(async <T>(
    operation: () => Promise<{ data?: T; error?: ApiError }>,
    config: RetryConfig = {}
  ): Promise<{ data?: T; error?: ApiError }> => {
    const finalConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
    let lastError: ApiError | null = null;
    
    setRetryCount(0);
    setIsRetrying(false);

    for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
      try {
        const result = await operation();
        
        if (result.error) {
          lastError = result.error;
          
          // Handle authentication errors immediately
          await handleAuthError(result.error);
          
          // Check if error is retryable and we haven't exceeded max retries
          if (attempt < finalConfig.maxRetries && isRetryableError(result.error, config)) {
            setIsRetrying(true);
            setRetryCount(attempt + 1);
            
            // Calculate delay with exponential backoff
            const delay = finalConfig.retryDelay * Math.pow(finalConfig.backoffMultiplier, attempt);
            await sleep(delay);
            continue;
          }
          
          // Return error if not retryable or max retries exceeded
          setIsRetrying(false);
          return {
            error: {
              ...result.error,
              message: getErrorMessage(result.error),
            }
          };
        }
        
        // Success - reset retry state and return data
        setIsRetrying(false);
        setRetryCount(0);
        return result;
        
      } catch (error: any) {
        lastError = {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred',
          retryable: false,
        };
        
        // Don't retry unexpected errors
        break;
      }
    }
    
    setIsRetrying(false);
    return {
      error: lastError || {
        code: 'UNKNOWN_ERROR',
        message: 'An unexpected error occurred',
      }
    };
  }, [handleAuthError, isRetryableError, getErrorMessage, sleep]);

  const handleError = useCallback((error: ApiError): string => {
    handleAuthError(error);
    return getErrorMessage(error);
  }, [handleAuthError, getErrorMessage]);

  return {
    executeWithRetry,
    handleError,
    getErrorMessage,
    isRetrying,
    retryCount,
    isRetryableError,
  };
}

export default useErrorHandler;